/* eslint-disable import/prefer-default-export */
import {
    ProblemDurationUnit,
    ProblemEpisodicity,
    ProblemSignificance,
    ProblemStatus,
    NormalRangeType,
} from "@emisgroup/clint-templates-common/lib/types";

export const clinicalcodeenGBMessages = {
    Button: {
        Add: "Add",
        Cancel: "Cancel",
        Update: "Update",
    },
    general: {
        and: "and",
        value: "value",
    },
    normalRange: {
        title: "Normal Range",
        enterNormalRange: "Enter a normal range",
        editNormalRange: "Edit normal range",
        normalRangeIs: "Normal value range is",
        values: {
            [NormalRangeType.None]: "",
            [NormalRangeType.Between]: "Between",
            [NormalRangeType.GreaterThan]: "Greater than",
            [NormalRangeType.GreaterThanOrEqualTo]: "Greater than or equal to",
            [NormalRangeType.LessThan]: "Less than",
            [NormalRangeType.LessThanOrEqualTo]: "Less than or equal to",
        },
        selectRangeType: "Select range type",
        invalidRange: "Range must be between a lower and upper limit",
        deleteRange: "Delete range",
        enterLowerLimit: "Enter lower limit",
        enterUpperLimit: "Enter upper limit",
        xToY: "{{x}} to {{y}}",
    },
    selectedCode: {
        addAnother: "Add Another",
        clickToAddAnother: "click to add another code",
        add: "Add",
        clickToAdd: "click to add code",
        cancel: "Cancel",
        clickToCancel: "click to cancel",
        close: "Close",
        clickToDeselect: "click to deselect",
        warnings: {
            title: "Warnings",
            header: "Warnings unavailable",
            disclaimer:
                "Medication warnings triggered when a code is selected are not available in EMIS Document Management. Users are advised to check for warnings in EMIS Web",
            preferredTerm:
                "<0>{{preferred}}</0> is the preferred term for <0>{{selected}}</0>. This has been set as the default.",
        },
    },
    qualifierEntry: {
        qualifiers: "Qualifiers",
        add: "Add a qualifier",
        select: "Select qualifier value",
        remove: "remove qualifier value",
        validateTextLength: "Only {{maximumCharacters}} characters allowed, your text has {{valueLength}} characters",
        invalid: "Invalid",
        unsupported:
            "This code has a qualifier of {{qualifierDescription}} that cannot currently be displayed. You can continue without this qualifier.",
    },
    dateEntry: {
        date: "Date",
        validateDateEmpty: "Please enter a date",
        validateDateFuture: "Date cannot be in the future",
        validateInvalidDate: "Please enter a valid date",
    },
    associatedTextEntry: {
        associatedText: "Associated text",
        validateTextEmpty: "Please enter text",
        validateTextLength: "Only {{maximumCharacters}} characters allowed, your text has {{valueLength}} characters",
    },
    numericEntry: {
        value: "Value",
        compound: "Value",
        enter: "enter value",
        enterTheValue: "Enter the {{value}}",
        abnormal: "Abnormal value",
        betweenXAndY: "between {{x}} and {{y}}",
        atLeastX: "greater than or equal to {{x}}",
        atMostX: "less than or equal to {{x}}",
        invalidRange: "The {{value}} must be {{description}}",
    },
    problemEntry: {
        problemDetail: "Problem detail",
        recordAsProblem: "Record as problem",
        platformError: "Error reading problem data",
        readingData: "Reading problem data",
        episodicity: {
            [ProblemEpisodicity.First]: "First Episode",
            [ProblemEpisodicity.New]: "New episode",
            [ProblemEpisodicity.FlareUp]: "Flare up",
            [ProblemEpisodicity.Review]: "Review",
            [ProblemEpisodicity.Ended]: "End episode",
        },
        status: {
            [ProblemStatus.ActiveProblem]: "Active",
            [ProblemStatus.HealthAdministration]: "Health administration",
            [ProblemStatus.Past]: "Past/ended",
        },
        significance: {
            [ProblemSignificance.Minor]: "Minor",
            [ProblemSignificance.Significant]: "Significant",
        },
        warnings: {
            "re-open":
                "There are no active problems for this code. There are past problems. The following will be reopened:",
            "no-active-problem": "No active problem exists",
            date: "Date",
            significance: "Significance",
            status: "Status",
            narrative: "Narrative",
            code: "Code",
        },
        additional: {
            dateMissing: "Please enter {{dateDescription}}",
            dateFuture: "{{dateDescription}} cannot be in the future",
            endDate: "End date",
            onsetDate: "Onset date",
            enterEndDateActive: "Please enter an end date for the active problem",
            "existing-problem-header": "Problem already exists",
            "existing-problem": "There is already an existing active problem for this code. What do you want to do?",
            "existing-problem-add": "Continue to add as a new problem",
            "existing-problem-add-end-existing": "Add this problem and end the existing one with an end date of",
            "onset-date-header": "New problem without onset date",
            "onset-date": "This problem doesn't yet exist. Add an onset date for this problem.",
            "original-onset-date-header": "Review date before onset date",
            "original-onset-date": "The review date is before this problem's onset date.",
            "original-onset-date-line-2": "- To add a review to this problem, change the date to after the onset date",
            "original-onset-date-line-3": "- To add a new problem for this code, add another onset date",
            onsetBeforeReview: "Onset date must be before review date",
        },
        duration: {
            unselected: "Remains active for...",
            custom: {
                choice: "Custom",
                chosen: "Remains active for",
                enter: "Enter the duration number of days",
                missing: "Enter the duration",
                invalid: "The number of days must be between 1 and 10,000",
                unit: "days",
            },
            unit: {
                [`${ProblemDurationUnit.Indefinite}`]: "Indefinitely",
                [`${ProblemDurationUnit.Indefinite}-chosen`]: "Remains active indefinitely",
                [`${ProblemDurationUnit.Day}-singular`]: "1 day",
                [`${ProblemDurationUnit.Day}-singular-chosen`]: "Remains active for 1 day",
                [`${ProblemDurationUnit.Day}-plural`]: "{{value}} days",
                [`${ProblemDurationUnit.Day}-plural-chosen`]: "Remains active for {{value}} days",
                [`${ProblemDurationUnit.Month}-singular`]: "1 month",
                [`${ProblemDurationUnit.Month}-singular-chosen`]: "Remains active for 1 month",
                [`${ProblemDurationUnit.Month}-plural`]: "{{value}} months",
                [`${ProblemDurationUnit.Month}-plural-chosen`]: "Remains active for {{value}} months",
                [`${ProblemDurationUnit.Year}-singular`]: "1 year",
                [`${ProblemDurationUnit.Year}-singular-chosen`]: "Remains active for 1 year",
                [`${ProblemDurationUnit.Year}-plural`]: "{{value}} years",
                [`${ProblemDurationUnit.Year}-plural-chosen`]: "Remains active for {{value}} years",
            },
        },
    },
};
